/**
 * Video Schema Utilities
 * Detect and generate VideoObject structured data for videos in HTML content
 * Supports: YouTube, Vimeo, TikTok, Instagram Reels, Facebook Reels, MP4
 */

export interface VideoData {
  type: 'youtube' | 'youtube-short' | 'tiktok' | 'instagram-reel' | 'facebook-reel' | 'vimeo' | 'mp4' | 'other'
  embedUrl?: string
  contentUrl?: string
  videoId?: string
  thumbnailUrl?: string
  duration?: string // ISO 8601 duration format (e.g., "PT3M10S")
  uploadDate?: string
  name?: string
  description?: string
  isShortForm?: boolean // Indicates if this is short-form content (under 60s)
}

/**
 * Extract all videos from HTML content
 */
export function extractVideosFromContent(htmlContent: string): VideoData[] {
  const videos: VideoData[] = []

  // 1. YouTube embeds (iframe)
  const youtubeIframeRegex = /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/g
  let match
  while ((match = youtubeIframeRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    videos.push({
      type: 'youtube',
      videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}`,
      contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
      thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
    })
  }

  // 2. YouTube watch links
  const youtubeWatchRegex = /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/g
  while ((match = youtubeWatchRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    if (!videos.some(v => v.videoId === videoId)) {
      videos.push({
        type: 'youtube',
        videoId,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
        thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
      })
    }
  }

  // 3. YouTube short links (youtu.be)
  const youtubeShortRegex = /youtu\.be\/([a-zA-Z0-9_-]+)/g
  while ((match = youtubeShortRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    if (!videos.some(v => v.videoId === videoId)) {
      videos.push({
        type: 'youtube',
        videoId,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
        thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
      })
    }
  }

  // 4. YouTube Shorts
  const youtubeShortsRegex = /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/g
  while ((match = youtubeShortsRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    if (!videos.some(v => v.videoId === videoId)) {
      videos.push({
        type: 'youtube-short',
        videoId,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        contentUrl: `https://www.youtube.com/shorts/${videoId}`,
        thumbnailUrl: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
        isShortForm: true,
      })
    }
  }

  // 5. TikTok videos
  const tiktokRegex = /tiktok\.com\/(?:@[\w.-]+\/video\/|v\/|embed\/v2\/)(\d+)/g
  while ((match = tiktokRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    videos.push({
      type: 'tiktok',
      videoId,
      embedUrl: `https://www.tiktok.com/embed/v2/${videoId}`,
      contentUrl: `https://www.tiktok.com/video/${videoId}`,
      isShortForm: true,
    })
  }

  // 6. Instagram Reels
  const instagramReelRegex = /instagram\.com\/(?:reel|reels)\/([A-Za-z0-9_-]+)/g
  while ((match = instagramReelRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    videos.push({
      type: 'instagram-reel',
      videoId,
      embedUrl: `https://www.instagram.com/reel/${videoId}/embed`,
      contentUrl: `https://www.instagram.com/reel/${videoId}`,
      isShortForm: true,
    })
  }

  // 7. Facebook Reels
  const facebookReelRegex = /facebook\.com\/(?:reel|reels)\/(\d+)/g
  while ((match = facebookReelRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    videos.push({
      type: 'facebook-reel',
      videoId,
      embedUrl: `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/reel/${videoId}`,
      contentUrl: `https://www.facebook.com/reel/${videoId}`,
      isShortForm: true,
    })
  }

  // 8. Vimeo embeds
  const vimeoRegex = /vimeo\.com\/(?:video\/)?(\d+)/g
  while ((match = vimeoRegex.exec(htmlContent)) !== null) {
    const videoId = match[1]
    videos.push({
      type: 'vimeo',
      videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}`,
      contentUrl: `https://vimeo.com/${videoId}`,
    })
  }

  // 9. Self-hosted MP4 files
  const mp4Regex = /(https?:\/\/[^\s"'<>]+\.mp4)/g
  while ((match = mp4Regex.exec(htmlContent)) !== null) {
    const videoUrl = match[1]
    videos.push({
      type: 'mp4',
      contentUrl: videoUrl,
      embedUrl: videoUrl,
    })
  }

  // 10. Video tags with source
  const videoTagRegex = /<video[^>]*>[\s\S]*?<source[^>]+src=["']([^"']+)["'][^>]*>[\s\S]*?<\/video>/g
  while ((match = videoTagRegex.exec(htmlContent)) !== null) {
    const videoUrl = match[1]
    if (!videos.some(v => v.contentUrl === videoUrl)) {
      videos.push({
        type: videoUrl.endsWith('.mp4') ? 'mp4' : 'other',
        contentUrl: videoUrl,
        embedUrl: videoUrl,
      })
    }
  }

  return videos
}

/**
 * Normalize date to ISO 8601 format with timezone
 */
export function normalizeDateWithTimezone(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    return new Date().toISOString()
  }
  return date.toISOString()
}

/**
 * Extract duration from article text
 * Looks for patterns like "3 minutes", "3:10", "3m10s"
 */
export function extractDurationFromText(text: string): string | undefined {
  // Pattern 1: "X:YY" format (most specific, check first)
  const pattern1 = /(\d+):(\d{2})/
  const match1 = text.match(pattern1)
  if (match1) {
    const minutes = parseInt(match1[1], 10)
    const seconds = parseInt(match1[2], 10)
    return `PT${minutes}M${seconds}S`
  }

  // Pattern 2: "X minutes Y seconds"
  const pattern2 = /(\d+)\s*(?:minutes?|min)\s*(?:et\s*)?(\d+)\s*(?:secondes?|seconds?|sec)/i
  const match2 = text.match(pattern2)
  if (match2) {
    const minutes = parseInt(match2[1], 10)
    const seconds = parseInt(match2[2], 10)
    return `PT${minutes}M${seconds}S`
  }

  // Pattern 3: "X minutes" (without seconds)
  const pattern3 = /(\d+)\s*(?:minutes?|min)\b/i
  const match3 = text.match(pattern3)
  if (match3) {
    const minutes = parseInt(match3[1], 10)
    return `PT${minutes}M0S`
  }

  // Pattern 4: "X seconds" (standalone)
  const pattern4 = /\b(\d+)\s*(?:secondes?|seconds?|sec|s)\b/i
  const match4 = text.match(pattern4)
  if (match4) {
    const seconds = parseInt(match4[1], 10)
    return `PT${seconds}S`
  }

  return undefined
}

/**
 * Generate VideoObject structured data for a video
 */
export function generateVideoSchema(
  video: VideoData,
  article: {
    title: string
    excerpt: string
    date: string
    modified: string
    slug: string
    featuredImage: string
  },
  baseUrl: string,
  siteName: string = 'Site'
): any {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${baseUrl}#video`,
    name: video.name || article.title,
    description: video.description || article.excerpt,
    thumbnailUrl: [
      video.thumbnailUrl || article.featuredImage,
      article.featuredImage,
    ].filter(Boolean),
    uploadDate: normalizeDateWithTimezone(video.uploadDate || article.date),
    contentUrl: video.contentUrl,
    embedUrl: video.embedUrl,
    duration: video.duration,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: baseUrl,
    },
    author: {
      '@type': 'Organization',
      name: siteName,
    },
    isFamilyFriendly: true,
  }
}

/**
 * Main function: Detect videos and generate all VideoObject schemas
 */
export function generateVideoSchemas(
  htmlContent: string,
  article: {
    title: string
    excerpt: string
    content: string
    date: string
    modified: string
    slug: string
    featuredImage: string
  },
  baseUrl: string,
  siteName: string = 'Site'
): any[] {
  const videos = extractVideosFromContent(htmlContent)

  if (videos.length === 0) {
    return []
  }

  // Try to extract duration from article text
  const plainText = htmlContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ')
  const duration = extractDurationFromText(plainText)

  return videos.map((video) => {
    const enhancedVideo = {
      ...video,
      duration: video.duration || duration,
      name: video.name || article.title,
      description: video.description || article.excerpt,
      uploadDate: video.uploadDate || article.date,
    }

    return generateVideoSchema(enhancedVideo, article, baseUrl, siteName)
  })
}

/**
 * Check if content contains any videos
 */
export function hasVideos(htmlContent: string): boolean {
  const videos = extractVideosFromContent(htmlContent)
  return videos.length > 0
}
